import React from 'react';
import logger from '../../utils/logger';

/**
 * Error Boundary component to catch JavaScript errors in child components
 * and display a fallback UI instead of crashing the entire app.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(_error) {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      errorId,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    this.setState({ error, errorInfo });

    // Log to our centralized logging service
    logger.error('React Error Boundary caught an error', {
      errorId: this.state.errorId,
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      componentName: this.props.componentName || 'Unknown',
      props: this.props.logProps ? this.props : undefined,
    });

    // Keep console.error for development debugging
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.group('🚨 React Error Boundary');
      logger.error('Error:', error);
      logger.error('Error Info:', errorInfo);
      logger.error('Error ID:', this.state.errorId);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI when an error occurs
      return (
        <div className="error-boundary-fallback" style={styles.container}>
          <div style={styles.content}>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              We apologize for the inconvenience. An unexpected error occurred.
              {this.state.errorId && (
                <span style={styles.errorId}>
                  {' '}Error ID: {this.state.errorId}
                </span>
              )}
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={styles.details}>
                <summary>Error Details (Development Only)</summary>
                <p><strong>Error ID:</strong> {this.state.errorId}</p>
                <pre style={styles.pre}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            <div style={styles.buttons}>
              <button onClick={this.handleRetry} style={styles.buttonPrimary}>
                Try Again
              </button>
              <button onClick={this.handleReload} style={styles.buttonSecondary}>
                Reload Page
              </button>
              <button onClick={this.handleGoHome} style={styles.buttonTertiary}>
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Render children normally when no error
    return this.props.children;
  }
}

// Inline styles for the error boundary
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '20px',
    backgroundColor: '#f5f5f5',
  },
  content: {
    maxWidth: '600px',
    textAlign: 'center',
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  title: {
    color: '#d32f2f',
    marginBottom: '16px',
    fontSize: '24px',
  },
  message: {
    color: '#666',
    marginBottom: '24px',
    fontSize: '16px',
  },
  details: {
    marginBottom: '24px',
    textAlign: 'left',
  },
  pre: {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '12px',
    maxHeight: '200px',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  buttonPrimary: {
    padding: '12px 24px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  buttonSecondary: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#1976d2',
    border: '1px solid #1976d2',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  buttonTertiary: {
    padding: '12px 24px',
    backgroundColor: '#636e72',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  errorId: {
    fontFamily: 'monospace',
    backgroundColor: '#f1f3f4',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '14px',
    color: '#5f6368',
  },
};

export default ErrorBoundary;
